/**
 * Utterance schemas (AGENT.md §6.3–6.4).
 *
 * Utterances are a design-time artifact: the Designer breaks a segment into one
 * or more utterances, each with a script (`text`), a speaker, a register, and
 * optional director's notes / reasoning question. The recorder later voices
 * each utterance into a `Recording`.
 *
 * FIREWALL note (§11.3): the recorder is shown the script, speaker, notes,
 * register, and (for the reasoning task) the question to voice — but never the
 * gold translation, gold answer, or challenge descriptions. Those are
 * challenge-level metadata and are deliberately absent from `UtteranceSummary`.
 */

import { z } from 'zod';
import { registerLabelSchema, difficultyLevelSchema } from './primitives.js';

/** One utterance as authored in the design step. */
export const utteranceInputSchema = z.object({
  ordinal: z.number().int().min(0, 'Ordinal must be zero or greater.'),
  speakerId: z.string().min(1, 'A speaker label is required.').max(120),
  utteranceContext: z.string().max(8000).default(''),
  /** The script the recorder reads aloud (metadata zone; recorder-permitted). */
  text: z.string().max(8000).default(''),
  registerLabel: registerLabelSchema,
  /** Gold form of the reasoning question; voiced into the question recording. */
  questionText: z.string().max(8000).optional(),
  assignedRecorderId: z.string().optional(),

  // Per-utterance gold (json_template submission format, §6.4). METADATA zone —
  // optional/additive; the design wizard fills these, the firewall keeps them
  // from the model, and they are gated behind can_view_gold on read.
  referenceTranslation: z.string().max(8000).optional(),
  referenceInterpretation: z.string().max(8000).optional(),
  referenceAnswer: z.string().max(8000).optional(),
  translationLevel: difficultyLevelSchema.optional(),
  reasoningLevel: difficultyLevelSchema.optional(),
  translationChallengeDescription: z.string().max(8000).optional(),
  reasoningChallengeDescription: z.string().max(8000).optional(),
});
export type UtteranceInput = z.infer<typeof utteranceInputSchema>;
export type UtteranceInputRequest = z.input<typeof utteranceInputSchema>;

/** Body for `POST /challenges/:id/utterances` — replaces the whole set. */
export const utteranceSetSchema = z
  .object({
    utterances: z.array(utteranceInputSchema).min(1, 'Add at least one utterance.').max(50),
  })
  .refine(
    ({ utterances }) => new Set(utterances.map((u) => u.ordinal)).size === utterances.length,
    { message: 'Utterance ordinals must be unique.', path: ['utterances'] },
  );
export type UtteranceSetInput = z.infer<typeof utteranceSetSchema>;
export type UtteranceSetRequest = z.input<typeof utteranceSetSchema>;

/**
 * Recorder/queue view of an utterance. The per-utterance gold fields below are
 * METADATA (§14): populated only for an actor holding `can_view_gold` (e.g. the
 * designer or a judge), otherwise `null` — exactly like the challenge-level gold.
 */
export interface UtteranceSummary {
  id: string;
  ordinal: number;
  speakerId: string;
  utteranceContext: string;
  text: string;
  registerLabel: string;
  /** The reasoning question to voice, if any (recorder needs it to record it). */
  questionText: string | null;
  assignedRecorderId: string | null;
  /** Per-utterance gold — null unless the actor holds `can_view_gold`. */
  referenceTranslation: string | null;
  referenceInterpretation: string | null;
  referenceAnswer: string | null;
  translationLevel: 'HARD' | 'EXTRA_HARD' | null;
  reasoningLevel: 'HARD' | 'EXTRA_HARD' | null;
  translationChallengeDescription: string | null;
  reasoningChallengeDescription: string | null;
}
